"use client";

import { useEffect, type ReactNode } from "react";
import { usePathname, useRouter } from "next/navigation";
import { useAuth } from "@/providers/auth.provider";
import Loader from "@/components/Loader";

const ONBOARDING_PATH = "/onboarding";

function FullScreenLoader() {
  return (
    <div className="fixed inset-0 flex h-screen w-screen items-center justify-center bg-background">
      <div className="text-primary">
        <Loader size={64} />
      </div>
    </div>
  );
}

/**
 * Mount inside `(authenticated)` layout. Redirects to /onboarding when the
 * current user's account hasn't completed the wizard yet. Renders a quiet
 * loader while the user payload is still in flight to avoid flashing the
 * sidebar before bouncing.
 *
 * Users without an `account` relation (e.g. super-admins) pass through —
 * they have nothing to onboard.
 */
export function RequireOnboardingComplete({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const userLoaded = !!user;
  const needsOnboarding =
    userLoaded && user?.account?.onboardingCompleted === false;

  useEffect(() => {
    if (needsOnboarding && pathname !== ONBOARDING_PATH) {
      router.replace(ONBOARDING_PATH);
    }
  }, [needsOnboarding, pathname, router]);

  if (!userLoaded) return <FullScreenLoader />;
  if (needsOnboarding) return <FullScreenLoader />;
  return <>{children}</>;
}

/**
 * Mount inside `(onboarding)` layout. Sends already-onboarded accounts (and
 * users without an account relation) back home so they can't reopen the
 * wizard after completing it.
 */
export function RequireOnboardingIncomplete({
  children,
}: {
  children: ReactNode;
}) {
  const { user } = useAuth();
  const router = useRouter();
  const userLoaded = !!user;
  const shouldBounceHome =
    userLoaded && user?.account?.onboardingCompleted !== false;

  useEffect(() => {
    if (shouldBounceHome) router.replace("/");
  }, [shouldBounceHome, router]);

  if (!userLoaded) return <FullScreenLoader />;
  if (shouldBounceHome) return <FullScreenLoader />;
  return <>{children}</>;
}
